import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to handle background opacity based on scroll position
 * @param {Object} options Configuration options
 * @param {number} options.transitionDistance Distance in pixels to complete the transition
 * @param {number} options.defaultDarkOpacity Default opacity for dark mode
 * @param {number} options.defaultLightOpacity Default opacity for light mode
 * @param {number} options.targetOpacity Target opacity when scrolled past threshold
 * @param {boolean} options.isDarkMode Current theme mode
 * @returns {string} The calculated background color with opacity
 */
const useScrollOpacity = ({
                              transitionDistance = 200,
                              defaultDarkOpacity = 0.72,
                              defaultLightOpacity = 0.81,
                              targetOpacity = 1,
                              isDarkMode = false
                          }) => {
    // State to hold the current background color
    const [backgroundMediaColor, setBackgroundMediaColor] = useState(
        isDarkMode
        ? `rgba(0,0,0,${defaultDarkOpacity})`
        : `rgba(255,255,255,${defaultLightOpacity})`
    );

    // Memoize the scroll handler to prevent unnecessary re-renders
    const handleScroll = useCallback(() => {
        const scrollPosition = window.scrollY;
        let opacity;

        if (scrollPosition <= 0) {
            // At the top - use default opacity
            opacity = isDarkMode ? defaultDarkOpacity : defaultLightOpacity;
        } else if (scrollPosition >= transitionDistance) {
            // Past the threshold - use target opacity
            opacity = targetOpacity;
        } else {
            // In transition - calculate intermediate opacity
            const progress = scrollPosition / transitionDistance;
            const startOpacity = isDarkMode ? defaultDarkOpacity : defaultLightOpacity;
            opacity = startOpacity + (targetOpacity - startOpacity) * progress;
        }

        // Format with correct number of decimal places
        opacity = Math.round(opacity * 100) / 100;

        // Update the color string based on the current theme
        const newBackgroundColor = isDarkMode
                                   ? `rgba(0,0,0,${opacity})`
                                   : `rgba(255,255,255,${opacity})`;

        setBackgroundMediaColor(newBackgroundColor);
    }, [isDarkMode, defaultDarkOpacity, defaultLightOpacity, targetOpacity, transitionDistance]);

    // Set up the scroll event listener
    useEffect(() => {
        // Calculate initial color
        handleScroll();

        // Add scroll event listener
        window.addEventListener('scroll', handleScroll);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    // Update when isDarkMode changes
    useEffect(() => {
        handleScroll();
    }, [isDarkMode, handleScroll]);

    return backgroundMediaColor;
};

export default useScrollOpacity;
